import {Grid, ThemeProvider} from "@mui/material";
import CategorySidebar from "./category-sidebar/CategorySidebar";
import React, {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import Box from "@mui/material/Box";
import CategorySelect from "./category-select/CategorySelect";
import DisplayWishes from "./display-wishes/DisplayWishes";
import {getWishes} from "../../store/wish/ActionCreators";
import {RootState, useAppDispatch, useAppSelector} from "../../store";
import {WishSortOptions, WishStatus, WishType} from "../../store/wish/Helper";
import Button from "@mui/material/Button";
import {getCategories} from "../../store/category/ActionCreators";
import {whiteInputsTheme} from "../../util/theme/whiteInputsTheme";
import useProgressiveInput from "../../hooks/useProgressiveInput";
import {useTranslation} from "react-i18next";
import ReactGA from "react-ga4";
import PageTitle from "../shared/page-title/PageTitle";
import {useLocation, useNavigate} from "react-router-dom";
import {metaTrack} from "../../util/metaPixel";

const Wishes = () => {
    const dispatch = useAppDispatch();
    const wishes = useAppSelector((state: RootState) => state.wishReducer.items);

    const {t} = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // ---- UI/filter state ----
    const [selectedCategory, setSelectedCategory] = useState<string>("all");
    const [selectedCity, setSelectedCity] = useState<string>("all");
    const [wishSorting, setWishSorting] = useState<string>(`${WishSortOptions.END_TIME},asc`);
    const [page, setPage] = useState(1);
    const [type, setType] = useState<WishType | undefined>(WishType.DRAW);

    // search: tmp (input) vs debounced applied
    const [searchText, setSearchText] = useState("");
    const [tmpSearchText, setTmpSearchText] = useState("");

    const [scheduleDebouncedSearch] = useProgressiveInput((value: string) => {
        setPage(1);
        setSearchText(value);
        ReactGA.event("search", {search_term: value});
        if (value.trim()) {
            metaTrack("Search", {search_string: value.trim()});
        }
    }, 1000);

    // ---- hydration + URL sync control ----
    const hasHydratedFromUrl = useRef(false);
    const [ready, setReady] = useState(false);

    /**
     * Single effect that:
     * 1) On first mount: reads query params and hydrates state
     * 2) After that: keeps the URL in sync with current state
     */
    useEffect(() => {
        // 1) First run – hydrate from URL
        if (!hasHydratedFromUrl.current) {
            const params = new URLSearchParams(location.search);

            const category = params.get("category");
            const city = params.get("city");
            const sort = params.get("sort");
            const pageParam = params.get("page");
            const typeParam = params.get("type") as WishType | null;
            const q = params.get("q");

            if (category) setSelectedCategory(category);
            if (city) setSelectedCity(city);
            if (sort) setWishSorting(sort);

            if (pageParam) {
                const parsed = parseInt(pageParam, 10);
                if (!Number.isNaN(parsed) && parsed > 0) {
                    setPage(parsed);
                }
            }

            if (typeParam && Object.values(WishType).includes(typeParam)) {
                setType(typeParam);
            }

            if (q) {
                setSearchText(q);
                setTmpSearchText(q);
            }

            hasHydratedFromUrl.current = true;
            setReady(true); // now we can start fetching
            return;
        }

        // 2) After hydration – keep URL in sync with state
        const params = new URLSearchParams();

        if (selectedCategory !== "all") params.set("category", selectedCategory);
        if (selectedCity !== "all") params.set("city", selectedCity);
        if (wishSorting !== `${WishSortOptions.END_TIME},asc`) {
            params.set("sort", wishSorting);
        }
        if (page > 1) params.set("page", String(page));
        if (type) params.set("type", type);
        if (searchText) params.set("q", searchText);

        const nextSearch = params.toString();
        const currentSearch = location.search.startsWith("?")
            ? location.search.slice(1)
            : location.search;

        if (nextSearch === currentSearch) {
            return;
        }

        navigate(
            {
                pathname: location.pathname,
                search: nextSearch ? `?${nextSearch}` : "",
                hash: location.hash,
            },
            {replace: true, state: location.state}
        );
    }, [
        navigate,
        location.hash,
        location.pathname,
        location.search, // only matters for first run
        location.state,
        selectedCategory,
        selectedCity,
        wishSorting,
        page,
        type,
        searchText
    ]);

    // ---- Fetch categories when type changes ----
    useEffect(() => {
        if (!ready) return; // avoid initial default-type fetch before URL hydration
        dispatch(getCategories({type, includeEmpty: false, sorted: true}));
        // dispatch(getCitiesWithCounts({type, sorted: true}));
    }, [dispatch, ready, type]);

    // ---- Centralized wishes fetching ----
    const params = useMemo(() => {
        return {
            category_id: selectedCategory === "all" ? undefined : selectedCategory,
            city: selectedCity === "all" ? undefined : selectedCity,
            limit: page * 6,
            page: 0,
            searchText,
            sorting: wishSorting,
            status: WishStatus.ACTIVE,
            type
        };
    }, [selectedCategory, page, searchText, wishSorting, type, selectedCity]);

    useEffect(() => {
        if (!ready) return; // wait until we've hydrated from URL

        dispatch(getWishes({...params, loadMore: false}));
    }, [dispatch, params, page, ready]);

    // ---- Handlers that only mutate state ----
    const handleSortingChange = (value: WishSortOptions) => {
        setWishSorting(value);
        setPage(1);
    };

    const handleChangePage = (_event: ChangeEvent<unknown>, nextPage: number) => {
        setPage(nextPage);
    };

    const handleChangeSearchText = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setTmpSearchText(value);
        scheduleDebouncedSearch(value);
    };

    const applySearchText = (value: string) => {
        setTmpSearchText(value);
        setPage(1);
        setSearchText(value);
        ReactGA.event("search", {search_term: value});
        if (value.trim()) {
            metaTrack("Search", {search_string: value.trim()});
        }
    };

    const handleChangeCategory = useCallback((id: string) => {
        const nextId = id.trim() || "all";
        setSelectedCategory(nextId);
        setPage(1);
    }, []);

    /*const handleChangeCity = useCallback((id: string) => {
        const nextId = id.trim() || "all";
        setSelectedCity(nextId);
        setPage(1);
    }, []);*/

    // ---- Derived UI flags ----
    const hasMore = wishes?.data?.length < wishes.count

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <PageTitle title={t("wishes")} sx={{display: {md: "none"}, mb: "0.75em"}}/>
            <Grid container spacing={4}>
                <Grid item md={3} sx={{display: {xs: "none", md: "block"}}}>
                    <CategorySidebar
                        selectedCategory={selectedCategory}
                        handleChangeCategory={handleChangeCategory}
                    />
                    {/*<CitySidebar
                        selectedCity={selectedCity}
                        handleChangeCity={handleChangeCity}
                    />*/}
                </Grid>

                <Grid item xs={12} md={9}>
                    <Grid item xs={12} sx={{display: {xs: "flex", md: "none"}}} mb="1em">
                        <CategorySelect
                            selectedCategory={selectedCategory}
                            disableCategoriesWithoutWishes
                            handleChangeCategory={handleChangeCategory}
                        />
                    </Grid>
                    {/*<Grid item xs={12} sx={{display: {xs: "flex", md: "none"}}} mb="1em">
                        <CitySelect
                            selectedCity={selectedCity}
                            handleChangeCity={handleChangeCity}
                        />
                    </Grid>*/}

                    <DisplayWishes
                        wishSorting={wishSorting}
                        handleSortingChange={handleSortingChange}
                        searchText={tmpSearchText}
                        type={type}
                        setType={(next) => {
                            setType(next);
                            setPage(1);
                            setSelectedCategory("all");
                            setSelectedCity("all");
                        }}
                        handleChangeSearchText={handleChangeSearchText}
                        applySearchText={applySearchText}
                    />

                    {hasMore ? (
                        <Box textAlign="center" my="2em">
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={(e) => handleChangePage(e as any, page + 1)}
                            >
                                {t("load_more")}
                            </Button>
                        </Box>
                    ) : null}
                </Grid>
            </Grid>
        </ThemeProvider>
    );
};

export default Wishes;
